/** Os lugares do acervo como opções do lançamento avulso (sem React, testável à parte). */
import type { LugaresDoAcervo } from '@/api/meusFeriados';
import type { LugarDoFormulario } from '@/components/feriados/FeriadoForm';
import { UF_COM_PREPOSICAO } from '@/constants/ufs';

const VAZIO = { tribunal: null, municipio_ibge: null, uf: null, justica: null };

function processos(n: number): string {
  return n === 1 ? '1 processo' : `${n} processos`;
}

function listaDeTribunais(tribunais: string[]): string {
  if (tribunais.length <= 1) return tribunais.join('');
  return `${tribunais.slice(0, -1).join(', no ')} e no ${tribunais[tribunais.length - 1]}`;
}

/**
 * Tribunais, depois comarcas, depois estados — cada grupo na ordem do backend (mais
 * processos primeiro). A comarca vale só na justiça dela; o estado, em qualquer justiça.
 */
export function opcoesDoAcervo(acervo: LugaresDoAcervo | undefined): LugarDoFormulario[] {
  if (!acervo) return [];
  const tribunais: LugarDoFormulario[] = acervo.tribunais.map((t) => ({
    value: `tribunal:${t.tribunal}`,
    label: `Todo o ${t.tribunal} · ${processos(t.processos)}`,
    searchText: t.tribunal,
    lugar: { ...VAZIO, abrangencia: 'tribunal', tribunal: t.tribunal },
  }));
  const comarcas: LugarDoFormulario[] = acervo.comarcas.map((c) => {
    const nome = c.municipio_nome
      ? `a comarca de ${c.municipio_nome}`
      : `o município IBGE ${c.municipio_ibge}${c.uf ? ` (${c.uf})` : ''}`;
    const onde = c.tribunais.length ? `, no ${listaDeTribunais(c.tribunais)}` : '';
    return {
      value: `municipal:${c.municipio_ibge}:${c.justica}`,
      label: `Só ${nome}${onde} · ${processos(c.processos)}`,
      searchText: [c.municipio_nome ?? String(c.municipio_ibge), ...c.tribunais, c.uf ?? ''].join(' '),
      lugar: { ...VAZIO, abrangencia: 'municipal', municipio_ibge: c.municipio_ibge, justica: c.justica },
    };
  });
  const ufs: LugarDoFormulario[] = acervo.ufs.map((u) => {
    const nome = u.uf === 'DF' ? 'Todo o Distrito Federal' : `Todo o estado ${UF_COM_PREPOSICAO[u.uf] ?? `(${u.uf})`}`;
    return {
      value: `estadual:${u.uf}`,
      label: `${nome}, em qualquer justiça · ${processos(u.processos)}`,
      searchText: `${u.uf === 'DF' ? 'Distrito Federal' : (UF_COM_PREPOSICAO[u.uf] ?? '').replace(/^d[aeo] /, '')} ${u.uf}`,
      lugar: { ...VAZIO, abrangencia: 'estadual', uf: u.uf },
    };
  });
  return [...tribunais, ...comarcas, ...ufs];
}
